import TokenMigrator from '@/utils/tokenMigrator';
import { getApiUrl, getSsoLoginUrl } from '@/config/apiConfig';

/**
 * 登录态校验服务
 */
class AuthService {
  /**
   * 从cookie中获取token
   * @param {string} name cookie名称
   * @returns {string|null} token值
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /**
   * 设置cookie
   * @param {string} name cookie名称
   * @param {string} value cookie值
   * @param {number} days 过期天数
   */
  setCookie(name, value, days = 7) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  }

  /**
   * 删除cookie
   * @param {string} name cookie名称
   */
  deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }

  /**
   * 尝试从cookie迁移token到localStorage
   * @returns {string|null} 迁移的token
   */
  migrateTokenFromCookie() {
    return TokenMigrator.migrateToLocalStorage('katoken', false);
  }

  /**
   * 获取token
   * @returns {string|null} token值
   */
  getToken() {
    // 优先从cookie中获取katoken（SSO登录后cookie会更新）
    const cookieToken = this.getCookie('katoken');
    if (cookieToken) {
      console.log('从cookie中获取到katoken');
      // 同时更新localStorage中的token，保持同步
      if (process.env.NODE_ENV === 'development') {
        localStorage.setItem('dev_katoken', cookieToken);
      } else {
        localStorage.setItem('katoken', cookieToken);
      }
      return cookieToken;
    }
    
    // 如果cookie中没有，再从localStorage获取
    const realToken = localStorage.getItem('katoken');
    if (realToken) {
      return realToken;
    }
    
    // 开发环境下如果没有真实token，可以尝试使用dev_katoken作为备用
    if (process.env.NODE_ENV === 'development') {
      const devToken = localStorage.getItem('dev_katoken');
      if (devToken) {
        console.warn('开发环境：使用dev_katoken作为备用token，建议使用真实katoken');
        return devToken;
      }
    }
    
    return null;
  }

  /**
   * 设置token
   * @param {string} token token值
   */
  setToken(token) {
    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem('dev_katoken', token)
    } else {
      localStorage.setItem('katoken', token)
    }
  }

  /**
   * 清除token
   */
  clearToken() {
    if (process.env.NODE_ENV === 'development') {
      localStorage.removeItem('dev_katoken')
    } else {
      localStorage.removeItem('katoken')
    }

    // 同时清除可能的cookie token
    const possibleTokenNames = ['katoken', 'token', 'access_token', 'auth_token', 'session_token', 'sso_token'];
    possibleTokenNames.forEach(name => this.deleteCookie(name));

    // 清除用户相关的缓存数据（退出登录时需要清空所有用户相关数据）
    const userRelatedKeys = [
      // 用户信息
      'currentStreamer',
      'streamers',
      'tiktok-live-room-id',
      'tiktok-binding-info',
      // 语言设置
      'auth-analysis',
      'auth-interaction',
      'selectedTargetLanguage',
      'dashboard_source_language',
      'dashboard_target_language',
      // Dashboard相关缓存
      'dashboard-activities',
      'dashboard-historical-comments',
      'dashboard-potential-patrons',
      'dashboard-last-comment-time',
      'dashboard-suggestions',
      'dashboard-current-suggestion-index',
      'dashboard-showing-single-suggestion',
      'dashboard-pending-batch-suggestion',
      'generatedScriptData',
      // 其他可能的用户缓存
      'accountBenefitInfo',
      'currentSessionId',
      'selectedUser',
      'referencedComments'
    ];

    userRelatedKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`已清除用户缓存数据: ${key}`);
      }
    });

    // 清除sessionStorage（如果有使用）
    try {
      sessionStorage.clear();
      console.log('已清除sessionStorage');
    } catch (error) {
      console.warn('清除sessionStorage失败:', error);
    }
  }

  /**
   * 设置开发环境token（仅开发环境使用）
   * @param {string} token 开发token
   */
  setDevToken(token) {
    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem('dev_katoken', token)
    }
  }

  /**
   * 生成开发环境token（仅开发环境使用）
   * @returns {string} 生成的token
   */
  generateDevToken() {
    if (process.env.NODE_ENV === 'development') {
      const timestamp = Date.now()
      const randomStr = Math.random().toString(36).substring(2, 15)
      const newToken = `dev_token_${timestamp}_${randomStr}`
      localStorage.setItem('dev_katoken', newToken)
      return newToken
    }
    return null
  }

  /**
   * 检查用户是否已登录
   * @returns {Promise<boolean>} 是否已登录
   */
  async checkLoginStatus() {
    try {
      // 获取token（会自动尝试从cookie迁移）
      const token = this.getToken()
      if (!token) {
        return false
      }

      const response = await fetch(getApiUrl('KSSO_IS_LOGIN'), {
        method: 'GET',
        headers: {
          'katoken': token,
          'Accept': '*/*',
          'Connection': 'keep-alive'
        }
      });

      if (!response.ok) {
        return false
      }

      const result = await response.json()
      console.log('登录状态检查结果:', result)
      return result.code === 200 && result.data === true
    } catch (error) {
      console.error('登录态校验失败:', error)
      return false
    }
  }

  /**
   * 跳转到登录页
   * @param {string} redirectUrl 重定向地址
   */
  redirectToLogin(redirectUrl = window.location.href) {
    const loginUrl = getSsoLoginUrl(redirectUrl);
    console.log('准备跳转到SSO登录页:', loginUrl);
    console.log('当前环境:', process.env.NODE_ENV);
    console.log('重定向地址:', redirectUrl);
    window.location.href = loginUrl;
  }

  /**
   * 退出登录
   * @param {string} redirectUrl 重定向地址
   */
  async logout(redirectUrl = window.location.origin) {
    // 开发环境直接重定向到首页
    if (process.env.NODE_ENV === 'development') {
      console.log('开发环境：退出登录，清除token');
      this.clearToken();
      window.location.href = redirectUrl;
      return;
    }

    try {
      // 先获取当前的token（用于SSO退出接口调用）
      const token = this.getToken();

      // 无论是否有token，都需要调用SSO退出接口来清除服务器端会话
      try {
        console.log('调用SSO退出接口，清理服务器端会话');
        await fetch(getApiUrl('KSSO_LOGOUT'), {
          method: 'GET',
          headers: {
            'katoken': token || '', // 如果没有token，传空字符串
            'Accept': '*/*',
            'Connection': 'keep-alive'
          }
        });
      } catch (ssoError) {
        console.warn('SSO退出接口调用失败，但继续执行本地清理:', ssoError);
      }

      // 清除本地所有存储的token和相关缓存数据
      console.log('清除本地所有token和缓存数据');
      this.clearToken();

      // 跳转到重定向地址
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('退出登录失败:', error);
      // 即使有错误也要清除本地token
      this.clearToken();
      // 跳转到重定向地址
      window.location.href = redirectUrl;
    }
  }

  /**
   * 初始化token（在应用启动时调用）
   */
  initializeToken() {
    // 尝试从cookie迁移token
    const migratedToken = this.migrateTokenFromCookie();
    if (migratedToken) {
      console.log('成功从cookie迁移token到localStorage');
    }
  }
}

export default new AuthService();
